import { useCallback, useReducer } from "react";

const initialState = {
  progress: 0,
  isUploading: false,
  failed: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case "START":
      return { ...state, isUploading: true, failed: [] };
    case "PROGRESS":
      return { ...state, progress: state.progress + action.increment };
    case "RESET_PROGRESS":
      return { ...state, progress: 0 };
    case "DONE":
      return { ...state, isUploading: false, failed: action?.failed ?? [] };
    case "RESET":
      return initialState;
    default:
      return state;
  }
};

/**
 * Manages the lifecycle and progress of a batch of async upload operations.
 * Each file's contribution to overall progress is weighted by its byte size,
 * so larger files move the progress bar more than smaller ones. Pre- and post-
 * hook function arrays allow callers to inject work that runs before or after
 * the main upload batch without re-implementing the progress logic.
 *
 * @param {object}     [options]
 * @param {Function[]} [options.preFns=[]]   - Async functions executed (in parallel) before the main upload batch.
 * @param {Function[]} [options.postFns=[]]  - Async functions executed (in parallel) after the main upload batch settles; each receives the allSettled results array.
 * @param {Function}   [options.onProgress]  - Called with (uploadResult, file) after each *successful* upload only; rejected uploads do not trigger this callback.
 *
 * @returns {{
 *   progress:    number,                            - Accumulated upload progress as a percentage (0–100); only successful uploads contribute.
 *   isUploading: boolean,                           - True while uploads are in flight.
 *   failed:      Array<{fn: Function, file: File}>, - Entries for each upload that rejected; empty when all succeeded.
 *   start:       (fns: Function[], files: File[]) => Promise<void>, - Kicks off the upload batch. Each fn is invoked as fn(file, { signal: AbortSignal }) where the signal auto-aborts after 2 minutes.
 *   reset:       () => void,                        - Restores all state to its initial values.
 * }}
 */
const useUploadProgress = ({ preFns = [], postFns = [], onProgress } = {}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const start = useCallback(
    async (fns = [], files = []) => {
      const totalBytes = files.reduce(
        (sum, file) => sum + (file?.size ?? 0),
        0,
      );

      dispatch({ type: "START" });
      dispatch({ type: "RESET_PROGRESS" });

      await Promise.all(preFns.map(fn => fn()));
      const results = await Promise.allSettled(
        fns.map(async (fn, idx) => {
          const file = files[idx];
          const increment =
            totalBytes > 0 ? ((file?.size ?? 0) / totalBytes) * 100 : 0;

          const result = await fn(file, {
            signal: AbortSignal.timeout(2 * 60 * 1000), // Auto-abort after 2m
          });

          dispatch({ type: "PROGRESS", increment });
          onProgress?.(result, file);

          return result;
        }),
      );

      const failed = fns
        .map((fn, idx) => ({ fn, file: files[idx] }))
        .filter((_, idx) => results[idx].status === "rejected");

      await Promise.all(postFns.map(fn => fn(results)));
      dispatch({ type: "DONE", failed });
    },
    [preFns, postFns, onProgress],
  );

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  return {
    ...state,
    start,
    reset,
  };
};

export default useUploadProgress;
